/**
 * Comprehensive conversion funnel tracking for Omnisend integration
 * Tracks user interactions across the entire signup and trial flow
 */

// Helper function to track Omnisend events (assumes existing integration)
const trackOmnisendEvent = (eventName: string, properties: any = {}) => {
  if (typeof window !== 'undefined' && window.omnisend) {
    window.omnisend.push(['track', eventName, {
      ...properties,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      page_url: window.location.href
    }]);
    console.log(`[ConversionTracker] Tracked event: ${eventName}`, properties);
  } else {
    console.warn(`[ConversionTracker] Omnisend not available, event not tracked: ${eventName}`);
  }
};

// Helper function to get stored user journey
const getStoredJourney = (): any[] => {
  const stored = localStorage.getItem('user_journey');
  return stored ? JSON.parse(stored) : [];
};

// Helper function to get original traffic source
const getOriginalSource = (): string => {
  return sessionStorage.getItem('original_source') || 
         localStorage.getItem('original_source') || 
         document.referrer || 
         'direct';
};

// Helper function to calculate signup progress percentage
const getSignupProgress = (): number => {
  const journey = getStoredJourney();
  const totalSteps = 5; // auth, form, terms, payment, completion
  const completedSteps = journey.filter(step => step.completed).length;
  return Math.round((completedSteps / totalSteps) * 100);
};

// Helper function to calculate time spent on current page
const calculateTimeSpent = (): number => {
  const startTime = sessionStorage.getItem('page_start_time');
  if (!startTime) return 0;
  return Math.round((Date.now() - parseInt(startTime)) / 1000);
};

/**
 * Main conversion tracking utilities
 */
export const ConversionTracker = {
  // Signup funnel events
  trackSignupStart: (source: string, context?: any) => {
    const properties = {
      source,
      context: context || 'unknown',
      referrer: document.referrer,
      utm_source: new URLSearchParams(window.location.search).get('utm_source'),
      utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
      utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign')
    };
    
    // Store original source for attribution
    if (!sessionStorage.getItem('original_source')) {
      sessionStorage.setItem('original_source', source);
      localStorage.setItem('original_source', source);
    }
    
    trackOmnisendEvent('signup_started', properties);
  },
  
  trackSignupAbandoned: (step: string, timeSpent: number, reason?: string) => {
    trackOmnisendEvent('signup_abandoned', {
      abandonment_step: step,
      time_spent_seconds: timeSpent,
      completion_percentage: getSignupProgress(),
      reason: reason || 'user_exit',
      source: getOriginalSource()
    });
  },
  
  trackTrialStarted: (planType: string, method: string = 'standard') => {
    trackOmnisendEvent('trial_started', {
      plan_type: planType,
      signup_method: method, // 'one_click', 'standard', 'auto'
      source: getOriginalSource(),
      journey_length: getStoredJourney().length
    });
  },
  
  trackTrialCompleted: (planType: string, userId: string) => {
    trackOmnisendEvent('trial_completed', {
      plan_type: planType,
      user_id: userId,
      source: getOriginalSource(),
      completion_time: new Date().toISOString()
    });
  },
  
  // Checkout flow events
  trackCheckoutStarted: (planId: string, context: string = 'pricing') => {
    trackOmnisendEvent('checkout_started', {
      plan_id: planId,
      context, // 'pricing', 'homepage', 'navbar', 'trial_upgrade'
      source: getOriginalSource()
    });
  },
  
  trackCheckoutAbandoned: (planId: string, timeSpent: number, step: string) => {
    trackOmnisendEvent('checkout_abandoned', {
      plan_id: planId,
      time_spent_seconds: timeSpent,
      abandonment_step: step, // 'form', 'payment', 'confirmation'
      source: getOriginalSource()
    });
  },
  
  trackPurchaseCompleted: (planId: string, amount: number, currency: string = 'USD') => {
    trackOmnisendEvent('purchase_completed', {
      plan_id: planId,
      amount,
      currency,
      source: getOriginalSource(),
      conversion_time: calculateTimeSpent()
    });
  },
  
  // Page interaction events
  trackCTAClick: (ctaType: string, location: string, planId?: string) => {
    trackOmnisendEvent('cta_clicked', {
      cta_type: ctaType, // 'trial_signup', 'view_pricing', 'login', 'get_started'
      location, // 'homepage', 'navbar', 'pricing', 'modal'
      plan_id: planId,
      source: getOriginalSource()
    });
  },
  
  trackFeatureInteraction: (feature: string, action: string) => {
    trackOmnisendEvent('feature_interaction', {
      feature_name: feature,
      action, // 'view', 'click', 'hover', 'expand'
      session_duration: calculateTimeSpent()
    });
  },
  
  // Trial experience events
  trackTrialOnboardingStep: (step: string, completed: boolean) => {
    trackOmnisendEvent('trial_onboarding_step', {
      step_name: step,
      completed,
      step_number: getSignupProgress()
    });
  },
  
  trackTrialValueRealization: (feature: string, value: string) => {
    trackOmnisendEvent('trial_value_realization', {
      feature_used: feature,
      value_achieved: value,
      trial_day: Math.floor((Date.now() - parseInt(localStorage.getItem('trial_start_time') || '0')) / (1000 * 60 * 60 * 24))
    });
  },
  
  // Error and support events
  trackError: (errorType: string, errorMessage: string, context: string) => {
    trackOmnisendEvent('error_encountered', {
      error_type: errorType,
      error_message: errorMessage,
      context,
      user_journey: getStoredJourney().slice(-3) // Last 3 steps
    });
  },
  
  trackSupportInteraction: (type: string, topic: string) => {
    trackOmnisendEvent('support_interaction', {
      interaction_type: type, // 'help_tooltip', 'faq_view', 'contact_form'
      topic,
      trial_status: localStorage.getItem('trial_status') || 'unknown'
    });
  }
};

export default ConversionTracker;